import React, { useState, useEffect, useMemo } from 'react';
import { getRecords } from './api/airtable';
import { useNavigate } from 'react-router-dom';

export default function LandingPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [records, setRecords] = useState([]);
  const navigate = useNavigate();

  // fetch all records once on mount
  useEffect(() => {
    (async () => {
      const data = await getRecords();
      setRecords(data);
    })();
  }, []);

  /** Utility: normalize a string for case‑insensitive comparison */
  const n = (str = '') => str.toLowerCase();

  /** Filtered + grouped + sorted data is memoised for performance */
  const grouped = useMemo(() => {
    // 1. optional search filter
    const filtered = records.filter(r => {
      if (!searchTerm) return true;
      const term = n(searchTerm);
      const f = r.fields;
      return (
        n(f['Full Name']).includes(term) ||
        n(f['Current Title on LinkedIn']).includes(term) ||
        n(f['Email']).includes(term)
      );
    });

    // 2. group by LEAP class (default "Unknown")
    const byClass = {};
    filtered.forEach(r => {
      const cls = r.fields['LEAP class'] || 'Unknown';
      if (!byClass[cls]) byClass[cls] = [];
      byClass[cls].push(r);
    });

    // 3a. sort classes: most recent first (assumes format YY‑YY)
    const classKeys = Object.keys(byClass).sort((a, b) => {
      const getStartYear = str => parseInt(str.split('-')[0], 10) || 0;
      return getStartYear(b) - getStartYear(a);
    });

    // 3b. sort students within class by last name
    classKeys.forEach(k => {
      byClass[k].sort((r1, r2) => {
        const ln = name => {
          const parts = name.trim().split(/\s+/);
          return parts[parts.length - 1].toLowerCase();
        };
        return ln(r1.fields['Full Name'] || '').localeCompare(ln(r2.fields['Full Name'] || ''));
      });
    });

    return { classKeys, byClass };
  }, [records, searchTerm]);

  return (
    <div className="min-h-screen bg-white p-8 font-sans">
      <header className="mb-4 text-3xl font-bold text-[#8C1515]">Students and Alumni</header>

      {/* search bar */}
      <div className="mb-6 flex flex-wrap gap-2">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name or email"
          className="w-full max-w-md flex-grow rounded border border-gray-300 p-2"
        />
        <button
          onClick={() => setSearchTerm('')} // clear search quickly
          className="rounded bg-[#8C1515] px-4 py-2 text-white hover:bg-[#A6192E]"
        >
          Clear
        </button>
      </div>

      {/* grouped cards */}
      {grouped.classKeys.map(cls => (
        <section key={cls} className="mb-10 w-full">
          <h2 className="mb-3 border-b-2 border-[#8C1515] pb-1 text-2xl font-semibold text-[#8C1515]">{cls}</h2>
          <div className="flex flex-wrap gap-4">
            {grouped.byClass[cls].map(rec => (
              <div
                key={rec.id}
                className="w-72 rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                <h3 className="mb-1 text-xl font-bold text-[#8C1515]">{rec.fields['Full Name'] || 'Unnamed'}</h3>
                <p className="text-sm text-gray-800"><span className="font-semibold">School / Job:&nbsp;</span>{rec.fields['Current Job or School'] || 'N/A'}</p>
                <button
                  onClick={() => navigate(`/details/${rec.id}`)}
                  className="mt-3 inline-block rounded-md bg-[#8C1515] px-4 py-2 text-white hover:bg-[#A6192E]"
                >
                  View Record
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}

      {grouped.classKeys.length === 0 && (
        <p className="text-gray-600">No students found.</p>
      )}
    </div>
  );
}
